import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useContent } from '../context/ContentProvider';
import { useTheme } from '../context/ThemeProvider';
import { Check, Clock, X, Loader2, Calendar, Users, Home, CreditCard, Mail } from 'lucide-react';
import { bookingService } from '../services/bookingService';
import { useRealtimeBookingStatus } from '../hooks/useRealtimeBookingStatus';

const Confirmation: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { content } = useContent();
  const { formData, selectedRooms, selectedExtras, resetBooking, hotelSlug, confirmationNumber, customerInfo, selectedHotel } = useBooking();
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  // Real-time booking status hook
  const { bookingStatus, isLoading: statusLoading, error: statusError } = useRealtimeBookingStatus(bookingId, confirmationNumber);
  
  // Create booking on component mount
  useEffect(() => {
    const createBooking = async () => {
      if (!formData.checkIn || !formData.checkOut || selectedRooms.length === 0) {
        navigate(`/${selectedHotel?.slug || 'hotel-adriatica'}`);
        return;
      }

      if (bookingId || confirmationNumber) return;

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        console.log('📋 Creating booking with data:', {
          formData,
          selectedRooms,
          selectedExtras
        });

        if (!selectedRooms[0]?.id) {
          throw new Error('Room ID is missing. Please select a room again.');
        }

        const nights = Math.ceil((formData.checkOut.getTime() - formData.checkIn.getTime()) / (1000 * 60 * 60 * 24));
        const roomsTotal = selectedRooms.reduce((total, room) => total + (room.price * room.count * nights), 0);
        const extrasTotal = selectedExtras.reduce((total, extra) => total + (extra.price * ('quantity' in extra ? extra.quantity || 1 : 1)), 0);
        const totalPrice = roomsTotal + extrasTotal;

        const mainRoom = selectedRooms[0];
        
        console.log('🏠 Using room for booking:', {
          id: mainRoom.id,
          type: mainRoom.type,
          price: mainRoom.price,
          count: mainRoom.count
        });

        const bookingData = {
          room_id: mainRoom.id,
          check_in: formData.checkIn,
          check_out: formData.checkOut,
          guest_name: formData.guestName || `${customerInfo.firstName} ${customerInfo.lastName}`,
          guest_email: formData.guestEmail || customerInfo.email,
          guest_phone: formData.guestPhone || customerInfo.phone,
          adults: parseInt(formData.adults || '1'),
          children: parseInt(formData.children || '0'),
          rooms_count: parseInt(formData.rooms || '1'),
          total_price: totalPrice,
          special_requests: formData.specialRequests || customerInfo.specialRequests || '',
          hotelSlug: hotelSlug,
          extras_info: selectedExtras.map(extra => ({
            id: extra.id,
            name: extra.name,
            price: extra.price,
            quantity: 'quantity' in extra ? extra.quantity : 1
          })),
          guest_address: formData.guestAddress || customerInfo.address,
          guest_city: formData.guestCity || customerInfo.city,
          guest_zipCode: formData.guestZipCode || customerInfo.zipCode,
          guest_country: formData.guestCountry || customerInfo.country,
        };

        console.log('📝 Final booking data being sent:', bookingData);

        const booking = await bookingService.createBooking(bookingData);
        setBookingId(booking.id);
      } catch (error) {
        console.error('❌ Booking creation failed:', error);
        setSubmitError(error instanceof Error ? error.message : 'Erreur lors de la création de la réservation');
      } finally {
        setIsSubmitting(false);
      }
    };

    createBooking();
  }, [formData, selectedRooms, selectedExtras, navigate, bookingId, confirmationNumber]);

  const handleNewBooking = () => {
    resetBooking();
    navigate(`/${selectedHotel?.slug || 'hotel-adriatica'}`);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return '';
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long',
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const calculateTotalNights = () => {
    if (!formData.checkIn || !formData.checkOut) return 0;
    return Math.ceil((formData.checkOut.getTime() - formData.checkIn.getTime()) / (1000 * 60 * 60 * 24));
  };

  const calculateTotalAmount = () => {
    const nights = calculateTotalNights();
    const roomsTotal = selectedRooms.reduce((total, room) => total + (room.price * room.count * nights), 0);
    const extrasTotal = selectedExtras.reduce((total, extra) => total + (extra.price * ('quantity' in extra ? extra.quantity ?? 1 : 1)), 0);
    return roomsTotal + extrasTotal;
  };
  
  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
        <Loader2 className="w-16 h-16 text-[var(--hotel-secondary-color)] animate-spin mb-4" />
        <h3 className="text-xl font-medium text-[var(--hotel-secondary-color)] mb-2">Création de votre réservation...</h3>
        <p className="text-gray-600 text-center">Veuillez patienter pendant que nous traitons votre demande.</p>
      </div>
    );
  }

  if (submitError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] py-12 px-4">
        <div className="bg-red-50 border border-red-200 p-6 rounded-[10px] text-center max-w-md">
          <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-700 mb-2">Erreur de réservation</h3>
          <p className="text-red-600 mb-4">{submitError}</p>
          <button 
            onClick={() => navigate('/customer')}
            className="px-6 py-2 bg-red-600 text-white rounded-[10px] hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Show booking status based on real-time updates
  const renderBookingStatus = () => {
    if (statusLoading && !bookingStatus) {
      return (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-[var(--hotel-secondary-color)] animate-spin mr-2" />
          <span className="text-[var(--hotel-secondary-color)]">Vérification du statut...</span>
        </div>
      );
    }

    if (statusError) {
      return (
        <div className="bg-orange-50 border border-orange-200 p-4 rounded-[10px]">
          <div className="flex items-center">
            <Clock className="w-6 h-6 text-orange-500 mr-2" />
            <span className="text-orange-700">Statut en cours de vérification...</span>
          </div>
        </div>
      );
    }

    if (!bookingStatus) {
      return (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-[10px]">
          <div className="flex items-center">
            <Clock className="w-6 h-6 text-blue-500 mr-2" />
            <span className="text-blue-700">Réservation en cours de traitement...</span>
          </div>
        </div>
      );
    }

    switch (bookingStatus.status) {
      case 'confirmed':
        return (
          <div className="bg-white p-8 text-center">
            <div className="w-20 h-20 bg-white border-4 border-[var(--hotel-secondary-color)] rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-12 h-12 text-[var(--hotel-secondary-color)]" strokeWidth={3} />
            </div>
            <h2 className="text-2xl font-semibold text-[var(--hotel-secondary-color)] mb-2">
              {content.confirmationTitle || "Réservation confirmée!"}
            </h2>
            <p className="text-gray-600 mb-4">
              {content.confirmationSubtitle || `Merci ${formData.guestName} pour votre réservation. Une confirmation a été envoyée à votre adresse email.`}
            </p>
            <div className="flex items-center justify-center space-x-6 mt-6">
              <div className="text-sm text-gray-700">
                <span className="font-medium">Numéro de réservation:</span>
                <span className="ml-2 font-semibold text-[var(--hotel-secondary-color)]">{bookingStatus.confirmation_number}</span>
              </div>
              <button
                onClick={handleNewBooking}
                className="px-6 py-2 bg-[var(--hotel-primary-color)] hover:bg-[var(--hotel-primary-color-hover)] text-white font-medium rounded-[10px] transition-colors"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        );
      
      case 'pending':
        return (
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-[10px]">
            <div className="flex items-center">
              <Clock className="w-6 h-6 text-yellow-500 mr-2" />
              <span className="text-yellow-700">Réservation en attente de confirmation...</span>
            </div>
          </div>
        );
      
      case 'cancelled':
        return (
          <div className="bg-red-50 border border-red-200 p-4 rounded-[10px]">
            <div className="flex items-center">
              <X className="w-6 h-6 text-red-500 mr-2" />
              <span className="text-red-700">Réservation annulée</span>
            </div>
          </div>
        );
      
      case 'failed':
        return (
          <div className="bg-red-50 border border-red-200 p-4 rounded-[10px]">
            <div className="flex items-center">
              <X className="w-6 h-6 text-red-500 mr-2" />
              <span className="text-red-700">Échec de la réservation</span>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="bg-gray-50 border border-gray-200 p-4 rounded-[10px]">
            <div className="flex items-center">
              <Clock className="w-6 h-6 text-gray-500 mr-2" />
              <span className="text-gray-700">Statut en cours de traitement...</span>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="py-8 px-4 bg-[#F5F3F0] min-h-screen">
      <div className="max-w-2xl mx-auto">
        {/* Status Card */}
        <div className="bg-white rounded-[10px] shadow-lg overflow-hidden mb-6">
          {renderBookingStatus()}
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-[10px] shadow-lg overflow-hidden">
          {/* Détails de votre réservation Header */}
          <div className=" bg-[var(--hotel-secondary-color)] px-6 py-4">
            <h2 className="text-lg font-semibold text-white">Détails de votre réservation</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-x-8 text-sm">
              {/* Section Headers in same row */}
              <div className="">
                <h3 className="font-semibold text-[var(--hotel-secondary-color)] flex items-center text-sm mb-1">
                  <Calendar className="w-4 h-4 mr-2" />
                  Dates du séjour
                </h3>
              </div>
              <div className="mb-2">
                <h3 className="font-semibold text-[var(--hotel-secondary-color)] flex items-center text-sm mb-1">
                  <Clock className="w-4 h-4 mr-2" />
                  Heures d'arrivée et de départ
                </h3>
              </div>

              {/* Dates content */}
              <div className="space-y-2">
                <div>
                  <div className="font-medium text-gray-700">Arrivée:{formatDate(formData.checkIn)}</div>
                </div>
                <div>
                  <div className="font-medium text-gray-700">Départ:{formatDate(formData.checkOut)}</div>
                </div>
                <div>
                  <div className="font-medium text-gray-700">Durée:{calculateTotalNights()} nuit{calculateTotalNights() > 1 ? 's' : ''}</div>
                </div>
              </div>

              {/* Hours content */}
              <div className="space-y-2">
                <div>
                  <div className="font-medium text-gray-700">Check-in:à partir de 15:00</div>
                </div>
                <div>
                  <div className="font-medium text-gray-700">Check-out:jusqu'à 11:00</div>
                </div>
              </div>

              {/* Hébergement */}
              <div className="col-span-2 mb-2 mt-4">
                <h3 className="font-semibold text-[var(--hotel-secondary-color)] flex items-center text-sm mb-3">
                  <Home className="w-4 h-4 mr-2" />
                  Hébergement
                </h3>
              </div>
              {selectedRooms.map((room, index) => {
                const nights = calculateTotalNights();
                const pricePerNight = room.price * room.count;
                const totalRoomPrice = pricePerNight * nights;

                return (
                  <React.Fragment key={index}>
                    <div className="font-medium text-gray-700">
                      <div>{room.type}</div>
                      <div className="text-xs text-gray-600">{room.count} nuit à {room.price}€ par nuit</div>
                    </div>
                    <div className="text-right font-semibold">{totalRoomPrice}€</div>
                  </React.Fragment>
                );
              })}

              {/* Services additionnels */}
              {selectedExtras.length > 0 && (
                <>
                  <div className="col-span-2 mb-2 mt-4">
                    <h3 className="font-semibold text-[var(--hotel-secondary-color)] text-sm mb-3">Services additionnels</h3>
                  </div>
                  {selectedExtras.map((extra, index) => (
                    <React.Fragment key={index}>
                      <div className="font-medium text-gray-700">{extra.name}</div>
                      <div className="text-right font-semibold">{extra.price * ('quantity' in extra ? extra.quantity ?? 1 : 1)}€</div>
                    </React.Fragment>
                  ))}
                </>
              )}

              {/* Total */}
              <div className="col-span-2 border-t border-gray-200 pt-3 mt-4">
                <div className="grid grid-cols-2 font-bold text-base">
                  <div>Total</div>
                  <div className="text-right">{calculateTotalAmount()}€</div>
                </div>
              </div>

              {/* Information du client */}
              <div className="col-span-2 mb-2 mt-6">
                <h3 className="font-semibold text-[var(--hotel-secondary-color)] flex items-center text-sm mb-3">
                  <Users className="w-4 h-4 mr-2" />
                  Information du client
                </h3>
              </div>
              <div className="font-medium text-gray-700">Nom</div>
              <div className="font-medium text-gray-700">Email</div>
              <div className="text-left">{formData.guestName}</div>
              <div className="text-left">{formData.guestEmail}</div>
              <div className="font-medium text-gray-700">Téléphone</div>
              <div className="font-medium text-gray-700">Adresse</div>
              <div className="text-left">{formData.guestPhone}</div>
              <div className="text-left text-sm">
                {formData.guestAddress || customerInfo.address}, {formData.guestZipCode || customerInfo.zipCode} {formData.guestCity || customerInfo.city}, {formData.guestCountry || customerInfo.country}
              </div>

              {/* Informations de paiement */}
              <div className="col-span-2 mb-2 mt-6">
                <h3 className="font-semibold text-[var(--hotel-secondary-color)] flex items-center text-sm mb-3">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Informations de paiement
                </h3>
              </div>
              <div className="font-medium text-gray-700">Méthode de paiement: Carte de crédit</div>

              <div className="col-span-2 p-3 rounded-md mt-2">
                <div className="text-sm text-gray-600">
                  Un prépaiement de 20% sera requis pour garantir votre réservation. Le solde sera réglé lors de votre séjour.
                </div>
              </div>

              {/* Besoin d'aide */}
              <div className="col-span-2  mt-6">
                <h3 className="font-semibold text-[var(--hotel-secondary-color)] flex items-center text-sm mb-1">
                  <Mail className="w-4 h-4 mr-2" />
                  Besoin d'aide?
                </h3>
              </div>
              <div className="col-span-2  px-4 rounded-md">
                <div className="text-gray-600 mb-3 text-sm">
                  Si vous avez des questions concernant votre réservation, n'hésitez pas à nous contacter:
                </div>
                <div className="grid grid-cols-2 gap-x-8  pb-4 text-sm">
                  <div className="font-medium col-span-2 text-gray-700">Email: <span className='text-[var(--hotel-secondary-color)]'>{theme.contactEmail || "reservation@hotel-adriatica.com"}</span></div>
                  {/* <div className="text-right"></div> */}
                  <div className="font-medium col-span-2 text-gray-700">Téléphone:<span className='text-[var(--hotel-secondary-color)]'> {theme.contactPhone || "+41 22 703 53 83"}</span></div>
                  {/* <div className="text-right"></div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;